import { useState } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, RefreshCw, Quote } from "lucide-react";
import { toast } from "sonner";

const QUICK = ["Gør den kortere", "Mere personlig", "Mere salgsorienteret", "Fremhæv bæredygtighed", "Mindre corporate"];

export default function Brand() {
  const { t, contentLang } = useLocale();
  const location = useLocation();
  const [description, setDescription] = useState(location.state?.topic || "");
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/brand", { description, language, attachment_ids: attachments.map((a) => a.id) });
      setResult(data.result);
      setProjectId(data.project_id);
      toast.success(t("Brandfortælling klar — gemt automatisk"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const allText = result
    ? `${result.story}\n\n--- Kort version ---\n${result.short}\n\n--- Elevator pitch ---\n${result.pitch}\n\n--- Kernebudskab ---\n${result.core_message}\n\n--- Taglines ---\n${(result.taglines || []).join("\n")}`
    : "";

  return (
    <div>
      <PageHeader overline="Brandfortælling" title="Fortæl jeres historie skarpt"
        subtitle="Beskriv virksomheden kort — eller lad feltet stå tomt og brug jeres eksisterende profil. Aicom AI skriver en konkret, levende brandfortælling uden floskler." />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="brand-description" value={description} onChange={(e) => setDescription(e.target.value)}
          rows={3} placeholder="Valgfrit: beskriv virksomheden kort (lad stå tomt for at bruge jeres profil)" />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="brand-language" />
          <Button onClick={generate} disabled={loading} className="gap-2" data-testid="brand-generate-btn">
            <Sparkles className="h-4 w-4" /> {t("Skriv brandfortælling")}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Skriver jeres brandfortælling..." />}

      {result && (
        <div className="space-y-4" data-testid="brand-result">
          <div className="flex items-center justify-between">
            <CopyButton text={allText} label="Kopiér alt" testid="brand-copy-all" />
            <Button variant="outline" size="sm" onClick={generate} disabled={loading} className="gap-2" data-testid="brand-regenerate-btn">
              <RefreshCw className="h-3.5 w-3.5" /> {t("Generér ny version")}
            </Button>
          </div>

          <RefinePanel result={result} setResult={setResult} module="brand" projectId={projectId} quick={QUICK.map((q) => t(q))} language={language}
            subtitle="Har du ønsker til denne version? Skriv dem her — så retter Aicom AI fortællingen til." />

          <Surface testid="brand-story">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("Brandfortælling")}</p>
              <CopyButton text={result.story} testid="brand-copy-story" label="" />
            </div>
            <Textarea value={result.story} onChange={(e) => setResult({ ...result, story: e.target.value })}
              rows={16} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-sm" data-testid="brand-story-text" />
          </Surface>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Surface testid="brand-short">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("Kort version")}</p>
                <CopyButton text={result.short} testid="brand-copy-short" label="" />
              </div>
              <Textarea value={result.short} onChange={(e) => setResult({ ...result, short: e.target.value })}
                rows={5} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-sm" data-testid="brand-short-text" />
            </Surface>
            <Surface testid="brand-pitch">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("Elevator pitch")}</p>
                <CopyButton text={result.pitch} testid="brand-copy-pitch" label="" />
              </div>
              <Textarea value={result.pitch} onChange={(e) => setResult({ ...result, pitch: e.target.value })}
                rows={5} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0 text-sm" data-testid="brand-pitch-text" />
            </Surface>
          </div>

          <Surface testid="brand-core">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("Kernebudskab")}</p>
            <div className="flex items-start gap-3">
              <Quote className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
              <p className="font-display text-lg font-medium leading-snug flex-1" data-testid="brand-core-text">{result.core_message}</p>
              <CopyButton text={result.core_message} testid="brand-copy-core" label="" />
            </div>
          </Surface>

          <Surface testid="brand-taglines">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-4">{t("Tagline-forslag")}</p>
            <div className="space-y-2">
              {(result.taglines || []).map((t, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 group" data-testid={`brand-tagline-${i}`}>
                  <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm flex-1">{t}</p>
                  <CopyButton text={t} testid={`brand-copy-tagline-${i}`} label="" />
                </div>
              ))}
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
